import { SimClock, DEFAULT_SIM_HZ } from '@antification/core';

export class GameLoop {
  private updateFn: (dt: number) => void;
  private renderFn: () => void;
  private running = false;
  private paused = false;
  private lastTime = 0;
  private accumulator = 0;
  private frameId: number | null = null;

  /**
   * The canonical clock. Its rate is a world parameter, not a client
   * preference: a client that ticks at a different rate from the server is
   * running a different simulation, not a smoother one. See ADR-002.
   */
  private readonly clock: SimClock;

  // Time scaling for fast-forward testing
  private timeScale = 1;

  // FPS tracking
  private frameCount = 0;
  private fpsTime = 0;
  private currentFps = 0;

  constructor(
    updateFn: (dt: number) => void,
    renderFn: () => void,
    tickRateHz: number = DEFAULT_SIM_HZ
  ) {
    this.updateFn = updateFn;
    this.renderFn = renderFn;
    this.clock = new SimClock(tickRateHz);
  }

  /** Ticks per sim-second. */
  getTickRate(): number {
    return this.clock.hz;
  }

  /** Canonical ticks elapsed. The simulation's only notion of when it is. */
  getTick(): number {
    return this.clock.tick;
  }

  /**
   * How far the render is between the last tick and the next, in [0, 1).
   *
   * The renderer's input for interpolation. At 60 Hz nothing needs it — a tick
   * and a frame are near enough the same thing. At the canonical 6 Hz, drawing
   * entities at their raw tick positions makes them teleport six times a
   * second, and this is the number that fixes it without the renderer ever
   * touching sim state.
   */
  getAlpha(): number {
    return Math.min(1, this.accumulator / this.clock.tickMs);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.loop();
  }

  stop(): void {
    this.running = false;
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }

  pause(): void {
    this.paused = true;
  }

  resume(): void {
    this.paused = false;
    this.lastTime = performance.now();
    this.accumulator = 0;
  }

  isPaused(): boolean {
    return this.paused;
  }

  setTimeScale(scale: number): void {
    this.timeScale = Math.max(0.1, Math.min(10, scale));
  }

  getTimeScale(): number {
    return this.timeScale;
  }

  getFps(): number {
    return this.currentFps;
  }

  private loop = (): void => {
    if (!this.running) return;

    const currentTime = performance.now();
    let deltaTime = currentTime - this.lastTime;
    this.lastTime = currentTime;

    // FPS calculation
    this.frameCount++;
    this.fpsTime += deltaTime;
    if (this.fpsTime >= 1000) {
      this.currentFps = this.frameCount;
      this.frameCount = 0;
      this.fpsTime = 0;
    }

    if (!this.paused) {
      // Apply time scale
      deltaTime *= this.timeScale;

      // Cap delta to prevent spiral of death
      if (deltaTime > 250) {
        deltaTime = 250;
      }

      this.accumulator += deltaTime;

      // Fixed timestep updates. The timestep comes from the clock rather than
      // from the frame, so a slow frame produces more ticks, never a longer one.
      while (this.accumulator >= this.clock.tickMs) {
        this.updateFn(this.clock.advance());
        this.accumulator -= this.clock.tickMs;
      }
    }

    // Always render
    this.renderFn();

    this.frameId = requestAnimationFrame(this.loop);
  };
}
